/**
 * App.jsx — Hyring AI Platform
 * Root component: handles auth state and top-level routing
 * 
 * Dependencies: npm install react react-dom
 * Build: npm create vite@latest hyring-frontend -- --template react
 *        then replace src/ with these files
 */

import { useState, useEffect } from "react";
import { Login }        from "./pages/Login";
import { Dashboard }    from "./pages/Dashboard";

const API = import.meta.env.VITE_API_URL || '';

// ── Global CSS variables injected once at root ───────────────
const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&family=DM+Mono:wght@300;400;500&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  --bg:    #06080F;
  --sur:   #0D1219;
  --card:  #121A24;
  --card2: #161F2C;
  --b:     rgba(255,255,255,0.07);
  --b2:    rgba(255,255,255,0.13);
  --or:    #FF5C1A;
  --or2:   #FF7A3D;
  --gd:    #C9A84C;
  --tl:    #0B7A75;
  --bl:    #2563C4;
  --tx:    #DDE3EE;
  --mu:    #5C6B85;
  --sans:  'DM Sans', sans-serif;
  --mono:  'DM Mono', monospace;
  --r:     8px;
  --r2:    12px;
}

body {
  background: var(--bg);
  color: var(--tx);
  font-family: var(--sans);
  min-height: 100vh;
  -webkit-font-smoothing: antialiased;
}

::-webkit-scrollbar { width: 4px; height: 4px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: var(--b2); border-radius: 2px; }

/* ── Shared utility classes ── */
.card  { background: var(--card);  border: 1px solid var(--b);  border-radius: var(--r2); overflow: hidden; }
.card2 { background: var(--card2); border: 1px solid var(--b);  border-radius: var(--r2); overflow: hidden; }
.panel-hdr { padding: 14px 18px; border-bottom: 1px solid var(--b); display: flex; align-items: center; justify-content: space-between; }
.panel-title { font-size: 13px; font-weight: 600; }
.panel-body  { padding: 16px 18px; }

/* Buttons */
.btn { padding: 8px 16px; border-radius: var(--r); border: 1px solid var(--b2); background: transparent; color: var(--tx); font-family: var(--sans); font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.15s; }
.btn:hover { background: rgba(255,255,255,0.05); }
.btn:active { transform: scale(0.98); }
.btn:disabled { opacity: 0.4; cursor: not-allowed; }
.btn.primary { background: var(--or); border-color: var(--or); color: #fff; }
.btn.primary:hover { background: var(--or2); }
.btn.sm { padding: 5px 12px; font-size: 12px; }
.btn.ghost { border-color: transparent; }

/* Badges */
.badge { display: inline-flex; align-items: center; font-family: var(--mono); font-size: 10px; padding: 2px 8px; border-radius: 4px; border: 1px solid transparent; white-space: nowrap; }
.badge.or { background: rgba(255,92,26,0.1);  border-color: rgba(255,92,26,0.25);  color: var(--or); }
.badge.gr { background: rgba(74,222,128,0.1); border-color: rgba(74,222,128,0.25); color: #4ade80; }
.badge.bl { background: rgba(37,99,196,0.15); border-color: rgba(37,99,196,0.3);   color: #60a5fa; }
.badge.gd { background: rgba(201,168,76,0.1); border-color: rgba(201,168,76,0.25); color: var(--gd); }
.badge.tl { background: rgba(11,122,117,0.15);border-color: rgba(11,122,117,0.3);  color: #2dd4bf; }
.badge.mu { background: rgba(255,255,255,0.05);border-color: var(--b);             color: var(--mu); }
.badge.rd { background: rgba(248,113,113,0.1); border-color: rgba(248,113,113,0.25);color: #f87171; }

/* Tables */
.data-table { width: 100%; border-collapse: collapse; font-size: 12px; }
.data-table th { text-align: left; font-family: var(--mono); font-size: 10px; text-transform: uppercase; letter-spacing: 0.1em; color: var(--mu); padding: 8px 12px; border-bottom: 1px solid var(--b); font-weight: 400; }
.data-table td { padding: 10px 12px; border-bottom: 1px solid rgba(255,255,255,0.03); vertical-align: middle; }
.data-table tr:hover td { background: rgba(255,255,255,0.02); cursor: pointer; }
.data-table tr:last-child td { border-bottom: none; }

/* Inputs */
.input { background: var(--card2); border: 1px solid var(--b); border-radius: var(--r); padding: 9px 12px; font-family: var(--mono); font-size: 12px; color: var(--tx); outline: none; transition: border 0.15s; width: 100%; }
.input:focus { border-color: rgba(255,92,26,0.4); }
.input::placeholder { color: var(--mu); }
textarea.input { resize: vertical; line-height: 1.6; }

/* AI thinking dots */
.thinking { display: flex; gap: 3px; align-items: center; }
.thinking-dot { width: 4px; height: 4px; border-radius: 50%; background: var(--or); animation: tdot 0.8s infinite; }
.thinking-dot:nth-child(2) { animation-delay: 0.15s; }
.thinking-dot:nth-child(3) { animation-delay: 0.3s;  }
@keyframes tdot { 0%,100%{opacity:0.3;transform:scale(1)} 50%{opacity:1;transform:scale(1.3)} }

/* Stat cards */
.stat { background: var(--card); border: 1px solid var(--b); border-radius: var(--r2); padding: 16px; position: relative; overflow: hidden; }
.stat::before { content:''; position:absolute; top:0; left:0; right:0; height:2px; }
.stat.or::before { background: var(--or); } .stat.or .stat-val { color: var(--or); }
.stat.gd::before { background: var(--gd); } .stat.gd .stat-val { color: var(--gd); }
.stat.tl::before { background: var(--tl); } .stat.tl .stat-val { color: var(--tl); }
.stat.bl::before { background: var(--bl); } .stat.bl .stat-val { color: #60a5fa; }
.stat-val { font-size: 26px; font-weight: 700; line-height: 1; margin-bottom: 4px; }
.stat-lbl { font-family: var(--mono); font-size: 10px; color: var(--mu); text-transform: uppercase; letter-spacing: 0.1em; }
.stat-delta { font-family: var(--mono); font-size: 10px; color: #4ade80; margin-top: 4px; }
.stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }

/* Layout helpers */
.two-col   { display: grid; grid-template-columns: 1fr 1fr;     gap: 14px; }
.three-col { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px; }
.flex-center { display: flex; align-items: center; justify-content: center; }
.flex-between { display: flex; align-items: center; justify-content: space-between; }

@keyframes fadeUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
.animate-in { animation: fadeUp 0.3s ease both; }
`;

export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loginError, setLoginError] = useState('');
  const [loginTab, setLoginTab] = useState('password');

  const handleLogin = (userData, accessToken, refreshToken) => {
    localStorage.setItem('hyring_access_token', accessToken);
    localStorage.setItem('hyring_refresh_token', refreshToken);
    localStorage.setItem('hyring_user', JSON.stringify(userData));
    setLoginError('');
    setUser(userData);
  };

  useEffect(() => {
    // Inject global styles
    if (!document.getElementById('hyring-global')) {
      const style = document.createElement('style');
      style.id = 'hyring-global';
      style.textContent = GLOBAL_CSS;
      document.head.appendChild(style);
    }

    const verifyMagicLink = async token => {
      try {
        const r = await fetch(`${API}/api/auth/magic-link/verify?token=${encodeURIComponent(token)}`);
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || 'Invalid or expired magic link');
        handleLogin(d.user, d.access_token, d.refresh_token);
        window.history.replaceState({}, '', '/');
      } catch (err) {
        localStorage.removeItem('hyring_access_token');
        localStorage.removeItem('hyring_refresh_token');
        localStorage.removeItem('hyring_user');
        setLoginTab('magic');
        const msg = err instanceof TypeError
          ? 'Network error. Please check your connection and try again.'
          : (err.message || 'Invalid or expired magic link');
        setLoginError(msg);
        window.history.replaceState({}, '', '/');
      } finally {
        setLoading(false);
      }
    };

    const url = new URL(window.location.href);
    const isMagicLink = url.pathname === '/auth/magic';
    const magicToken = url.searchParams.get('token');
    if (isMagicLink) {
      if (!magicToken) {
        setLoginTab('magic');
        setLoginError('Invalid or expired magic link');
        window.history.replaceState({}, '', '/');
        setLoading(false);
        return;
      }
      verifyMagicLink(magicToken);
      return;
    }

    // Check for stored auth token
    const token = localStorage.getItem('hyring_access_token');
    const stored = localStorage.getItem('hyring_user');
    if (token && stored) {
      try { setUser(JSON.parse(stored)); } catch {}
    }
    setLoading(false);
  }, []);

  const handleLogout = async () => {
    const refreshToken = localStorage.getItem('hyring_refresh_token');
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken })
      });
    } catch {}
    localStorage.removeItem('hyring_access_token');
    localStorage.removeItem('hyring_refresh_token');
    localStorage.removeItem('hyring_user');
    setUser(null);
  };

  if (loading) return null;
  if (!user)   return <Login onLogin={handleLogin} initialError={loginError} initialTab={loginTab} />;
  return <Dashboard user={user} onLogout={handleLogout} />;
}
